import {translate} from '@docusaurus/Translate';
import type {ReactNode} from 'react';
import {lazy, Suspense, useCallback, useEffect, useRef, useState} from 'react';
import Link from '@docusaurus/Link';
import {useLocation} from '@docusaurus/router';
import useBaseUrl from '@docusaurus/useBaseUrl';

import {useAssistant} from './AssistantContext';
import AssistantMark from './AssistantMark';
import PageReferenceMenu from './PageReferenceMenu';
import {readPageContext} from './PageContextTracker';
import {appendPageReference, PAGE_REFERENCE_EVENT} from './assistantReference';
import styles from './styles.module.css';

const AssistantWorkspace = lazy(() => import('./AssistantWorkspace'));

export default function AssistantOverlay(): ReactNode {
  const location = useLocation();
  const {hasConsented, draft, setDraft, setSourceContext} = useAssistant();
  const [isOpen, setIsOpen] = useState(false);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const isAssistantPage = normalizedPath === useBaseUrl('/ai-docs').replace(/\/+$/, '');

  const closePanel = useCallback((restoreFocus = true) => {
    setIsOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => launcherRef.current?.focus());
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      if (hasConsented) {
        inputRef.current?.focus();
      }
    });
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return;
      }

      event.preventDefault();
      closePanel();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(frameId);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closePanel, hasConsented, isOpen]);

  useEffect(() => {
    if (isAssistantPage) {
      setIsOpen(false);
    }
  }, [isAssistantPage]);

  if (isAssistantPage) {
    return null;
  }

  function referenceContent(content: string): boolean {
    const context = readPageContext(`${location.pathname}${location.search}${location.hash}`);
    const nextDraft = appendPageReference(draft, context, content);
    if (nextDraft === null) {
      return false;
    }
    setSourceContext(context);
    setDraft(nextDraft);
    window.dispatchEvent(new Event(PAGE_REFERENCE_EVENT));
    setIsOpen(true);
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextDraft.length, nextDraft.length);
    });
    return true;
  }

  return (
    <div className={styles.overlayRoot} data-assistant-ui>
      <PageReferenceMenu onReference={referenceContent} />
      {isOpen && (
        <aside id="ai-docs-assistant-panel" className={styles.floatingPanel} aria-label={translate({message: 'FolderSpan 智能助理'})}>
          <header className={styles.panelHeader}>
            <div>
              <span className={styles.panelMark}>
                <AssistantMark />
              </span>
              <span>
                <small>FOLDERSPAN / DOC AI</small>
                <strong>{translate({message: '智能助理'})}</strong>
              </span>
            </div>
            <div className={styles.panelActions}>
              <Link to="/ai-docs" onClick={() => closePanel(false)} aria-label={translate({message: '在完整页面打开智能助理'})}>
                {translate({message: '完整页面'})}
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M5 11 11 5M6 5h5v5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </Link>
              <button type="button" onClick={() => closePanel()} aria-label={translate({message: '关闭智能助理'})}>
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="m4 4 8 8m0-8-8 8" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </button>
            </div>
          </header>
          <Suspense fallback={<p role="status">{translate({message: '正在加载智能助理…'})}</p>}>
            <AssistantWorkspace mode="panel" inputRef={inputRef} onDeclineConsent={closePanel} />
          </Suspense>
        </aside>
      )}

      <button
        type="button"
        className={styles.launcher}
        ref={launcherRef}
        aria-expanded={isOpen}
        aria-controls="ai-docs-assistant-panel"
        aria-label={isOpen ? translate({message: 'AI DOCS 收起助理'}) : translate({message: 'AI DOCS 问文档'})}
        onClick={() => (isOpen ? closePanel() : setIsOpen(true))}>
        <span className={styles.launcherIcon}>
          <AssistantMark />
          <i aria-hidden="true" />
        </span>
        <span>
          <small>AI DOCS</small>
          <strong>{isOpen ? translate({message: '收起助理'}) : translate({message: '问文档'})}</strong>
        </span>
      </button>
    </div>
  );
}
